<template>
<div class="platform-page">
  <HeadNav selectedLanguage="en" page="platform" />
  <main class="platform-page__main">
    <section
      v-for="(section, sectionIndex) in page.sections"
      :id="section.id"
      :key="section.id"
      class="vpn-section platform-section"
      :class="{
        'platform-section--alt': sectionIndex % 2 === 1,
        'platform-section--device-guides': section.id === 'need-a-vpn-on-another-device',
      }"
    >
      <div class="vpn-section__inner platform-section__inner">
        <h1
          v-if="sectionIndex === 0"
          class="vpn-section__title platform-section__title platform-section__title--page"
        >
          {{ section.title }}
        </h1>
        <h2 v-else class="vpn-section__title platform-section__title">{{ section.title }}</h2>

        <template v-for="(block, blockIndex) in section.blocks">
          <h3
            v-if="block.type === 'heading' || block.type === 'h3'"
            :key="`heading-${section.id}-${blockIndex}`"
            class="platform-subheading"
          >
            {{ block.text }}
          </h3>

          <p
            v-else-if="block.type === 'p'"
            :key="`html-${section.id}-${blockIndex}`"
            class="platform-paragraph"
            v-html="block.html"
          />

          <ul
            v-else-if="block.type === 'ul'"
            :key="`html-ul-${section.id}-${blockIndex}`"
            class="platform-list"
          >
            <li
              v-for="(item, itemIndex) in block.items"
              :key="`html-ul-item-${itemIndex}`"
              v-html="item"
            />
          </ul>

          <ol
            v-else-if="block.type === 'ol'"
            :key="`html-ol-${section.id}-${blockIndex}`"
            class="platform-list platform-list--ordered"
          >
            <li
              v-for="(item, itemIndex) in block.items"
              :key="`html-ol-item-${itemIndex}`"
              v-html="item"
            />
          </ol>

          <p
            v-else-if="block.type === 'paragraph'"
            :key="`paragraph-${section.id}-${blockIndex}`"
            class="platform-paragraph"
          >
            <template v-if="block.parts">
              <template v-for="(part, partIndex) in block.parts">
                <strong
                  v-if="part.type === 'strong'"
                  :key="`paragraph-strong-${partIndex}`"
                >{{ part.value }}</strong>
                <span v-else :key="`paragraph-text-${partIndex}`">{{ part.value }}</span>
              </template>
            </template>
            <template v-else>{{ block.text }}</template>
          </p>

          <p
            v-else-if="block.type === 'formula'"
            :key="`formula-${section.id}-${blockIndex}`"
            class="platform-formula"
          >
            <template v-if="block.parts">
              <template v-for="(part, partIndex) in block.parts">
                <strong
                  v-if="part.type === 'strong'"
                  :key="`formula-strong-${partIndex}`"
                >{{ part.value }}</strong>
                <span v-else :key="`formula-text-${partIndex}`">{{ part.value }}</span>
              </template>
            </template>
            <template v-else>{{ block.text }}</template>
          </p>

          <ol
            v-else-if="block.type === 'ordered-list'"
            :key="`ordered-${section.id}-${blockIndex}`"
            class="platform-list platform-list--ordered"
          >
            <li
              v-for="(item, itemIndex) in block.items"
              :key="`ordered-item-${itemIndex}`"
            >
              <template v-if="Array.isArray(item)">
                <template v-for="(part, partIndex) in item">
                  <strong
                    v-if="part.type === 'strong'"
                    :key="`ordered-strong-${itemIndex}-${partIndex}`"
                  >{{ part.value }}</strong>
                  <span v-else :key="`ordered-text-${itemIndex}-${partIndex}`">{{ part.value }}</span>
                </template>
              </template>
              <template v-else>{{ item }}</template>
            </li>
          </ol>

          <ul
            v-else-if="block.type === 'unordered-list'"
            :key="`unordered-${section.id}-${blockIndex}`"
            class="platform-list"
          >
            <li
              v-for="(item, itemIndex) in block.items"
              :key="`unordered-item-${itemIndex}`"
            >
              <template v-if="Array.isArray(item)">
                <template v-for="(part, partIndex) in item">
                  <strong
                    v-if="part.type === 'strong'"
                    :key="`unordered-strong-${itemIndex}-${partIndex}`"
                  >{{ part.value }}</strong>
                  <span v-else :key="`unordered-text-${itemIndex}-${partIndex}`">{{ part.value }}</span>
                </template>
              </template>
              <template v-else>{{ item }}</template>
            </li>
          </ul>

          <div
            v-else-if="block.type === 'table'"
            :key="`table-${section.id}-${blockIndex}`"
            class="platform-table-wrap"
          >
            <table class="platform-table">
              <thead>
                <tr>
                  <th v-for="header in block.headers" :key="header">{{ header }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, rowIndex) in block.rows" :key="rowIndex">
                  <td v-for="(cell, cellIndex) in row" :key="cellIndex">{{ cell }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>
      </div>
    </section>

    <FaqSection
      v-if="page.faqs && page.faqs.length"
      :title="page.faqTitle"
      :items="page.faqs"
    />

    <section v-if="page.closing" class="vpn-section platform-closing">
      <div class="vpn-section__inner platform-closing__inner">
        <h2>{{ page.closing.title }}</h2>
        <p
          v-for="(paragraph, paragraphIndex) in page.closing.paragraphs"
          :key="paragraphIndex"
        >
          {{ paragraph }}
        </p>
        <router-link class="vpn-btn vpn-btn--primary" to="/download/">
          {{ page.closing.ctaLabel }}
        </router-link>
        <p v-if="page.closing.links" class="platform-closing__links">
          {{ page.closing.linksPrefix }}
          <template v-for="(link, linkIndex) in page.closing.links">
            <span :key="`separator-${linkIndex}`" v-if="linkIndex > 0">
              {{ linkIndex === page.closing.links.length - 1 ? ' and ' : ', ' }}
            </span>
            <a
              v-if="link.external"
              :key="`external-${link.to}`"
              :href="link.to"
              :target="link.external ? '_blank' : null"
              :rel="link.external ? 'noopener noreferrer' : null"
            >
              {{ link.label }}
            </a>
            <router-link v-else :key="link.to" :to="link.to">
              {{ link.label }}
            </router-link>
          </template>
          {{ page.closing.linksSuffix }}
        </p>
      </div>
    </section>
  </main>
</div>
</template>

<script>
import '@/css/vpn-theme.scss'
import HeadNav from '@/components/HeadNav.vue'
import FaqSection from '@/components/vpn/FaqSection.vue'
import { myvpnbookPlatformPages } from '@/data/myvpnbookPlatformPages'

export default {
	name: 'PlatformPage',
	components: {
		HeadNav,
		FaqSection,
	},
	props: {
		pageKey: {
			type: String,
			required: true,
		},
	},
	computed: {
		page() {
			return myvpnbookPlatformPages[this.pageKey]
		},
	},
	mounted() {
		if (typeof document !== 'undefined') {
			document.body.classList.add('vpn-site')
		}
	},
	beforeDestroy() {
		if (typeof document !== 'undefined') {
			document.body.classList.remove('vpn-site')
		}
	},
}
</script>

<style lang="scss" scoped>
@import '@/css/vpn-sections.scss';

.platform-page {
  min-height: 70vh;
  max-width: 100%;
  overflow-x: hidden;
}

.platform-page__main {
  width: 100%;
  max-width: 100%;
  overflow-x: hidden;
}

.platform-section {
  background: #fff;
}

.platform-section--alt {
  background: linear-gradient(180deg, #faf8ff 0%, #f5f1ff 100%);
}

.platform-section--device-guides {
  padding: 56px 0;
  background:
    radial-gradient(circle at 12% 18%, rgba(74, 154, 255, 0.14), transparent 28%),
    radial-gradient(circle at 88% 82%, rgba(27, 111, 232, 0.08), transparent 30%),
    linear-gradient(180deg, #f7fbff 0%, #eef5ff 100%);
  border-top: 1px solid rgba(27, 111, 232, 0.1);
  border-bottom: 1px solid rgba(27, 111, 232, 0.1);
}

.platform-section--device-guides .platform-section__title {
  margin-bottom: 12px;
  font-size: clamp(26px, 3vw, 34px);
}

.platform-section--device-guides .platform-paragraph:first-of-type {
  margin-bottom: 0;
  color: #66758c;
  font-size: 15px;
}

.platform-section--device-guides .platform-list {
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px 16px;
  margin: 24px 0 26px;
}

.platform-section--device-guides .platform-list li {
  padding: 0;
  border: 1px solid #d9e8fb;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 8px 22px rgba(21, 88, 192, 0.05);
  overflow: hidden;
  transition: transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;

  &::before {
    display: none;
  }

  &:hover {
    transform: translateY(-1px);
    border-color: #a9ccf8;
    box-shadow: 0 12px 28px rgba(21, 88, 192, 0.1);
  }
}

.platform-section--device-guides ::v-deep .platform-list a {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-height: 56px;
  padding: 0 18px;
  color: #1558c0;
  font-weight: 700;
  text-decoration: none;

  &::after {
    content: '→';
    flex: none;
    color: var(--vpn-brand, #1b6fe8);
    font-size: 17px;
    line-height: 1;
  }
}

.platform-section--device-guides ::v-deep .platform-list + .platform-paragraph {
  margin: 0;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  color: #66758c;
  font-size: 14.5px;

  a {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 42px;
    margin-left: 4px;
    padding: 0 18px;
    border-radius: 999px;
    background: var(--vpn-brand, #1b6fe8);
    color: #fff;
    font-weight: 700;
    text-decoration: none;
    box-shadow: 0 8px 20px rgba(27, 111, 232, 0.24);
    transition: transform 0.2s ease, background 0.2s ease, box-shadow 0.2s ease;

    &:hover {
      transform: translateY(-1px);
      background: var(--vpn-brand-deep, #1558c0);
      color: #fff;
      box-shadow: 0 10px 24px rgba(27, 111, 232, 0.3);
    }
  }
}

.platform-section__title {
  margin-left: 0;
  margin-right: 0;
  text-align: left;
}

.platform-section__title--page {
  margin-bottom: 28px;
}

.platform-paragraph,
.platform-list {
  a {
    color: var(--vpn-brand, #5b4fe8);
    font-weight: 600;
  }
}

.platform-paragraph {
  margin: 0 0 18px;
  color: var(--vpn-ink-muted, #5b6475);
  font-size: 16px;
  line-height: 1.75;

  strong {
    color: var(--vpn-ink, #1f2430);
    font-weight: 700;
  }
}

.platform-subheading {
  margin: 30px 0 10px;
  color: var(--vpn-ink, #1f2430);
  font-size: 20px;
  line-height: 1.35;
  font-weight: 700;
}

.platform-formula {
  margin: 6px 0 24px;
  padding: 18px 22px;
  border-left: 4px solid var(--vpn-brand, #6d4aff);
  border-radius: 0 12px 12px 0;
  background: #f7f4ff;
  color: var(--vpn-ink, #1f2430);
  font-size: 16px;

  strong {
    font-weight: 700;
  }
  line-height: 1.6;
  font-weight: 700;
  overflow-wrap: anywhere;
}

.platform-list {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px 28px;
  margin: 18px 0 24px;
  padding: 0;
  list-style: none;
}

.platform-list li {
  position: relative;
  padding-left: 24px;
  color: var(--vpn-ink, #1f2430);
  font-size: 15px;
  line-height: 1.6;

  strong {
    font-weight: 700;
  }

  &::before {
    content: '';
    position: absolute;
    left: 2px;
    top: 8px;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--vpn-brand, #6d4aff);
    box-shadow: 0 0 0 4px rgba(109, 74, 255, 0.1);
  }
}

.platform-list--ordered {
  counter-reset: platform-step;
  grid-template-columns: 1fr;
}

.platform-list--ordered li {
  counter-increment: platform-step;
  padding-left: 42px;

  &::before {
    content: counter(platform-step);
    top: 0;
    left: 0;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    color: #fff;
    font-size: 13px;
    font-weight: 700;
    text-align: center;
    line-height: 28px;
    box-shadow: none;
  }
}

.platform-table-wrap {
  width: 100%;
  margin: 24px 0 28px;
  overflow-x: auto;
  border: 1px solid var(--vpn-line, #e8e4f8);
  border-radius: 14px;
  background: #fff;
}

.platform-table {
  width: 100%;
  min-width: 680px;
  border-collapse: collapse;
  text-align: left;

  th,
  td {
    padding: 15px 18px;
    border-bottom: 1px solid var(--vpn-line, #e8e4f8);
    color: var(--vpn-ink-muted, #5b6475);
    font-size: 14.5px;
    line-height: 1.55;
    vertical-align: top;
  }

  th {
    background: #f4f0ff;
    color: var(--vpn-ink, #1f2430);
    font-weight: 700;
  }

  tr:last-child td {
    border-bottom: 0;
  }
}

.platform-closing {
  background:
    radial-gradient(ellipse 60% 70% at 15% 20%, rgba(138, 63, 252, 0.12), transparent 60%),
    radial-gradient(ellipse 55% 65% at 90% 80%, rgba(62, 84, 232, 0.1), transparent 55%),
    linear-gradient(180deg, #f4f0ff 0%, #ebe4ff 100%);
}

.platform-closing__inner {
  text-align: center;

  h2 {
    margin: 0 0 14px;
    color: var(--vpn-ink, #1f2430);
    font-size: clamp(24px, 3vw, 34px);
    line-height: 1.2;
  }

  p {
    margin: 0 auto 18px;
    color: var(--vpn-ink-muted, #5b6475);
    font-size: 16px;
    line-height: 1.65;
  }

  .vpn-btn {
    margin-bottom: 22px;
  }
}

.platform-closing__links {
  a {
    color: var(--vpn-brand, #6d4aff);
    font-weight: 700;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
}

@media (max-width: 640px) {
  .platform-paragraph {
    font-size: 15px;
  }

  .platform-subheading {
    margin-top: 24px;
    font-size: 18px;
  }

  .platform-list,
  .platform-section--device-guides .platform-list {
    grid-template-columns: 1fr;
  }

  .platform-section--device-guides {
    padding: 40px 0;
  }

  .platform-section--device-guides ::v-deep .platform-list + .platform-paragraph {
    align-items: stretch;

    a {
      width: 100%;
      margin-left: 0;
    }
  }

  .platform-formula {
    padding: 14px 16px;
    font-size: 15px;
  }
}
</style>
